import type { WorkflowType } from '@innovationdepartment/proxima-sdk-axios';
import { AxiosError } from 'axios';
import useProximaSDK from 'hooks/useProximaSDK';
import { useBrandStore } from 'stores';
import type {
  BrandScheduler,
  ExecutionDetails,
  ExecutionSummary,
  LatestExecution
} from './BrandWorkflows.types';

function formatSdkError(error: unknown): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 'unknown';
    const data = error.response?.data;
    const bodyMessage =
      data != null &&
      typeof data === 'object' &&
      'message' in data &&
      String((data as { message: unknown }).message).trim();
    return bodyMessage
      ? `Request failed (${status}): ${bodyMessage}`
      : `Request failed (${status})`;
  }
  return error instanceof Error ? error.message : 'Request failed';
}

export type GetLatestExecutionsResult =
  | { data: LatestExecution[]; error?: undefined }
  | { data?: undefined; error: string };

export type GetExecutionHistoryResult =
  | { data: ExecutionSummary[]; error?: undefined }
  | { data?: undefined; error: string };

export type GetSchedulersResult =
  | { data: BrandScheduler[]; error?: undefined }
  | { data?: undefined; error: string };

export type GetExecutionResult =
  | { data: ExecutionDetails; error?: undefined }
  | { data?: undefined; error: string };

export type RetryFromFailedResult =
  | { success: true; error?: undefined }
  | { success?: undefined; error: string };

export type TriggerExecutionResult =
  | { data: { executionId: string }; error?: undefined }
  | { data?: undefined; error: string };

function useBrandWorkflowsApi() {
  const executionsApi = useProximaSDK('ExecutionsApi');
  const schedulersApi = useProximaSDK('SchedulersApi');
  const { brand } = useBrandStore();
  const { brandId } = brand;

  const workflowsApiUrl = import.meta.env.VITE_WORKFLOWS_API_URL;
  const notConfiguredError = 'Workflows API is not configured';

  const getLatestExecutions = async (): Promise<GetLatestExecutionsResult> => {
    if (!workflowsApiUrl) return { error: notConfiguredError };
    if (!brandId) return { error: 'Missing brandId' };

    try {
      const response = await executionsApi.getLatestExecutions({ brandId });
      /* the contract types status as a plain string; narrow it to the statuses the UI styles by */
      return { data: (response.data as LatestExecution[]) ?? [] };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const getExecutionHistory = async (
    workflowType: string,
    limit = 10
  ): Promise<GetExecutionHistoryResult> => {
    if (!workflowsApiUrl) return { error: notConfiguredError };
    if (!brandId) return { error: 'Missing brandId' };

    try {
      const response = await executionsApi.getExecutionHistory({
        brandId,
        workflowType: workflowType as WorkflowType,
        limit
      });
      return { data: (response.data as ExecutionSummary[]) ?? [] };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const getSchedulers = async (): Promise<GetSchedulersResult> => {
    if (!workflowsApiUrl) return { error: notConfiguredError };
    if (!brandId) return { error: 'Missing brandId' };

    try {
      const response = await schedulersApi.getBrandSchedulers({ brandId });
      return { data: response.data ?? [] };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const getExecution = async (executionId: string): Promise<GetExecutionResult> => {
    if (!workflowsApiUrl) return { error: notConfiguredError };

    try {
      const response = await executionsApi.getExecution({ executionId });
      if (response.data == null) return { error: 'No data in response' };
      return { data: response.data as ExecutionDetails };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const retryFromFailed = async (executionId: string): Promise<RetryFromFailedResult> => {
    if (!workflowsApiUrl) return { error: notConfiguredError };

    try {
      await executionsApi.retryFromFailed({ executionId });
      return { success: true };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  const triggerExecution = async (
    workflowType: string,
    triggerPayload: unknown
  ): Promise<TriggerExecutionResult> => {
    if (!workflowsApiUrl) return { error: notConfiguredError };
    if (!brandId) return { error: 'Missing brandId' };

    const normalizedTriggerPayload =
      triggerPayload != null && typeof triggerPayload === 'object'
        ? (triggerPayload as object)
        : undefined;

    try {
      const response = await executionsApi.triggerExecution({
        brandId,
        triggerExecutionRequest: {
          workflowType: workflowType as WorkflowType,
          triggerPayload: normalizedTriggerPayload
        }
      });
      if (response.data == null) return { error: 'No data in response' };
      return { data: response.data as { executionId: string } };
    } catch (e) {
      return { error: formatSdkError(e) };
    }
  };

  return {
    getLatestExecutions,
    getExecutionHistory,
    getSchedulers,
    getExecution,
    retryFromFailed,
    triggerExecution,
    loading: executionsApi.loading || schedulersApi.loading,
    brandId
  };
}

export default useBrandWorkflowsApi;
